import { useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { API } from "@/lib/api";
import { textOn, visibleOnDark } from "./FusionShared";

/** Fusion AI video experience.
 *  - style "voiceover": approved photo with Ken-Burns motion + AI voiceover (audio asset)
 *  - style "talking_head": fal.ai lip-synced mp4 (video asset) */
export function FusionVideoPlayer({ photoUrl, athleteName, school, logo, color, assetId, script, style = "voiceover", onPlay }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const isVideo = style === "talking_head";

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); onPlay && onPlay(); }
  };

  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10 select-none" data-testid="fusion-video-player">
      <div className="relative aspect-video bg-black overflow-hidden">
        {isVideo ? (
          <video
            src={`${API}/fusion/video-file/${assetId}`}
            controls playsInline preload="metadata"
            onPlay={() => onPlay && onPlay()}
            className="absolute inset-0 w-full h-full object-contain bg-black"
            data-testid="fusion-video-element"
          />
        ) : (
          <>
            {photoUrl ? (
              <img src={photoUrl} alt={athleteName}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ animation: playing ? "fusionKenBurns 18s ease-in-out infinite alternate" : "none" }} />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/30 text-sm">No approved photo</div>
            )}
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(5,10,20,0.85) 100%)" }} />
            <button onClick={toggle} data-testid="fusion-video-play-btn"
              className="absolute inset-0 m-auto h-16 w-16 rounded-full flex items-center justify-center transition-transform hover:scale-105"
              style={{ background: visibleOnDark(color), color: textOn(visibleOnDark(color)) }}>
              {playing ? <Pause className="h-7 w-7" /> : <Play className="h-7 w-7 ml-1" />}
            </button>
          </>
        )}
        {logo && <img src={logo} alt={school} className="absolute top-3 right-3 h-10 w-10 object-contain rounded bg-white/90 p-1 pointer-events-none" />}
        <div className={`absolute left-4 right-16 pointer-events-none ${isVideo ? "top-3" : "bottom-3"}`}>
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color }}>BioHacker Fusion™ · {school}</div>
          {!isVideo && <div className="text-xl font-black text-white">{athleteName}</div>}
        </div>
      </div>
      {script && (
        <div className="px-4 py-3 bg-white/5 text-xs text-white/60 italic border-t border-white/10" data-testid="fusion-video-script">
          "{script}"
        </div>
      )}
      {!isVideo && <audio ref={audioRef} src={`${API}/fusion/video-audio/${assetId}`} onEnded={() => setPlaying(false)} preload="none" />}
      <style>{`@keyframes fusionKenBurns { 0% { transform: scale(1) translate(0,0); } 100% { transform: scale(1.15) translate(-2%, -3%); } }`}</style>
    </div>
  );
}
